// Location: src/components/admin/schedule/ScheduleCommentsPerRun.tsx
// Purpose: Comments per run configuration for comment publishing schedules
// Scope: Dropdown selector with calculation display and tooltips

import React from 'react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { Schedule } from '@/types/schedules';

interface ScheduleCommentsPerRunProps {
	schedule: Schedule;
	isSuperAdmin: boolean;
	userOwnsSchedule: boolean;
	isTaskRunning: boolean;
	effectiveCommentsPerRun: string;
	displayReadyCount: number;
	pendingPostsCount: number;
	onCommentsPerRunChange: (value: string) => void;
	getDisplayAmount: () => number;
}

export const ScheduleCommentsPerRun = React.memo(
	function ScheduleCommentsPerRun({
		schedule,
		isSuperAdmin,
		userOwnsSchedule,
		isTaskRunning,
		effectiveCommentsPerRun,
		displayReadyCount,
		pendingPostsCount,
		onCommentsPerRunChange,
		getDisplayAmount,
	}: ScheduleCommentsPerRunProps) {
		if (
			schedule.type !== 'comments' &&
			schedule.type !== 'profile_comment'
		) {
			return null;
		}

		if (!isSuperAdmin && !userOwnsSchedule) {
			return null;
		}

		return (
			<TooltipProvider delayDuration={0}>
				<div className='flex flex-col gap-2 mb-4'>
					<div className='flex items-center gap-2'>
						<span className='text-lg font-semibold text-gray-900 dark:text-white'>
							Comments Per Run:
						</span>
						<Select
							value={effectiveCommentsPerRun}
							onValueChange={onCommentsPerRunChange}
							disabled={isTaskRunning}
						>
							<SelectTrigger className='w-[200px]'>
								<SelectValue placeholder='Select comments per run' />
							</SelectTrigger>
							<SelectContent>
								{isSuperAdmin && (
									<SelectItem value='ALL'>
										<div className='flex items-center gap-2'>
											<span>🔥</span>
											<div>
												<div className='font-medium'>
													ALL
												</div>
											</div>
										</div>
									</SelectItem>
								)}
								<SelectItem value='ONE'>
									<div className='flex items-center gap-2'>
										<span>⚡️</span>
										<div>
											<div className='font-medium'>
												ONE
											</div>
										</div>
									</div>
								</SelectItem>
								<SelectItem value='DISTRIBUTED'>
									<div className='flex items-center gap-2'>
										<span>📊</span>
										<div>
											<div className='font-medium'>
												DISTRIBUTED
											</div>
										</div>
									</div>
								</SelectItem>
							</SelectContent>
						</Select>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className='w-5 h-5 rounded-full bg-gradient-to-br from-pink-400 via-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold cursor-help hover:scale-105 transition-transform duration-200 shadow-sm'>
									?
								</div>
							</TooltipTrigger>
							<TooltipContent className='max-w-xs'>
								<div className='text-sm'>
									<div className='font-semibold mb-1'>
										Comment Publishing Options:
									</div>
									<div className='space-y-1'>
										<div>
											<span className='font-medium'>
												🚀 ALL:
											</span>{' '}
											Publish all available comments
											(ready + generated)
											{!isSuperAdmin && (
												<span className='text-xs text-gray-500 ml-2'>
													(Super Admin only)
												</span>
											)}
										</div>
										<div>
											<span className='font-medium'>
												🎯 ONE:
											</span>{' '}
											Publish one comment per run
										</div>
										<div>
											<span className='font-medium'>
												📊 DISTRIBUTED:
											</span>{' '}
											Spread comments naturally across 4
											daily runs
										</div>
									</div>
								</div>
							</TooltipContent>
						</Tooltip>
					</div>
					{/* Display calculated amount */}
					<div className='flex items-center gap-2 text-lg'>
						<span className='text-gray-600 dark:text-gray-400'>
							Next run will publish:
						</span>
						<span className='font-bold text-pink-500 bg-pink-50 dark:bg-pink-900/20 px-2 py-1 rounded-md'>
							{getDisplayAmount()} comment
							{getDisplayAmount() !== 1 ? 's' : ''}
						</span>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className='w-4 h-4 rounded-full bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold cursor-help hover:scale-105 transition-transform duration-200 shadow-sm'>
									?
								</div>
							</TooltipTrigger>
							<TooltipContent className='max-w-xs'>
								<div className='text-sm'>
									<div className='font-semibold mb-1'>
										📊 How Publishing Works:
									</div>
									<div className='space-y-1'>
										<div>
											•{' '}
											<span className='font-medium'>
												Ready comments:
											</span>{' '}
											{displayReadyCount} already
											generated
										</div>
										<div>
											•{' '}
											<span className='font-medium'>
												Pending posts:
											</span>{' '}
											{pendingPostsCount} can create new
											comments
										</div>
										<div>
											•{' '}
											<span className='font-medium'>
												Total available:
											</span>{' '}
											{displayReadyCount +
												pendingPostsCount}{' '}
											comments possible
										</div>
									</div>
								</div>
							</TooltipContent>
						</Tooltip>
					</div>

					{/* 🎭 HUMAN-LIKE BEHAVIOR: Display random delay info */}
					<div className='flex items-center gap-2 text-sm mt-2'>
						<span className='text-gray-500 dark:text-gray-400'>
							⏰ With random delays:
						</span>
						<span className='text-orange-600 dark:text-orange-400 font-medium bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded-md'>
							0-5s between comments
						</span>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className='w-4 h-4 rounded-full bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 flex items-center justify-center text-white text-xs font-bold cursor-help hover:scale-105 transition-transform duration-200 shadow-sm'>
									?
								</div>
							</TooltipTrigger>
							<TooltipContent className='max-w-xs'>
								<div className='text-sm'>
									<div className='font-semibold mb-1'>
										🎭 Human-like Publishing:
									</div>
									<div>
										Each comment is published with a random
										delay to appear more natural and avoid
										detection as automated posting.
									</div>
								</div>
							</TooltipContent>
						</Tooltip>
					</div>
				</div>
			</TooltipProvider>
		);
	}
);
